import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionBadge, formatAnalyzedAt } from './components/DecisionWidgets';
import { useDecisionPortfolio } from './DecisionPortfolioContext';

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(2)}万`;
  return n.toFixed(2);
}

function formatSyncHint(sync) {
  if (!sync) return '';
  if (sync.synced) {
    const hostLabel = sync.authHost === 'overseas' ? '野草云' : '腾讯';
    return `${sync.message || '已同步'}（来源：${hostLabel}）`;
  }
  if (sync.eligible && sync.message) {
    return `云端同步未完成：${sync.message}`;
  }
  return '';
}

export default function PortfolioPage() {
  const navigate = useNavigate();
  const {
    summaries,
    results,
    sync,
    updatedAt,
    loading,
    analyzing,
    error,
    analyzedOnce,
    ensureLoaded,
    syncFromCloud,
    analyzeAll
  } = useDecisionPortfolio();

  useEffect(() => {
    void ensureLoaded();
  }, [ensureLoaded]);

  return (
    <div>
      <h1 className="decision-page-title">我的持仓</h1>
      <p className="decision-page-sub">
        超级 VIP 自动从会员云端同步持仓（与小程序同源）。列表可先浏览，需要时再分析组合。
      </p>

      <div className="decision-card" style={{ marginBottom: 16 }}>
        <h3>会员云端持仓</h3>
        <p className="decision-muted" style={{ marginTop: 0 }}>
          管理台登录用户名需与会员库超级 VIP 一致；白名单走腾讯，名单外走野草云。
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="decision-btn" onClick={() => void syncFromCloud()} disabled={loading || analyzing}>
            {loading ? '同步中…' : '从会员云同步'}
          </button>
          <button
            type="button"
            className="decision-btn"
            onClick={() => void analyzeAll()}
            disabled={loading || analyzing || !summaries.length}
          >
            {analyzing ? '分析中…' : analyzedOnce ? '重新分析组合' : '分析组合'}
          </button>
          <button type="button" className="decision-btn ghost" onClick={() => void ensureLoaded({ force: true })} disabled={loading || analyzing}>
            刷新列表
          </button>
        </div>
        {updatedAt ? <p className="decision-muted" style={{ marginTop: 8 }}>快照时间 {updatedAt}</p> : null}
        {formatSyncHint(sync) ? <p className="decision-muted">{formatSyncHint(sync)}</p> : null}
        {error ? <p className="decision-error">{error}</p> : null}
      </div>

      <div className="decision-card">
        <h3>持仓列表</h3>
        {!summaries.length && !loading ? (
          <p className="decision-muted">暂无持仓。请先点「从会员云同步」。</p>
        ) : (
          <>
            <p className="table-hint">点击行进入产品分析；组合级结论需先「分析组合」</p>
            <table className="decision-table decision-table-interactive">
              <thead>
                <tr>
                  <th>产品</th>
                  <th>机构</th>
                  <th>占比</th>
                  <th>市值估算</th>
                  <th>综合分</th>
                  <th>建议</th>
                  <th>分析时间</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((row) => {
                  const analyzed = results.find((r) => String(r.product_id) === String(row.product_id));
                  return (
                    <tr
                      key={row.product_id}
                      onClick={() => {
                        const params = new URLSearchParams({ id: row.product_id });
                        if (row.agent_org) params.set('agent', row.agent_org);
                        navigate(`/product?${params.toString()}`);
                      }}
                    >
                      <td>{row.product_name}</td>
                      <td>{row.agent_org || '—'}</td>
                      <td>{row.weight_pct != null ? `${row.weight_pct}%` : '—'}</td>
                      <td>{formatMoney(row.market_value)}</td>
                      <td>{analyzed?.score ?? '—'}</td>
                      <td>
                        {analyzed ? (
                          <ActionBadge action={analyzed.action} label={analyzed.label} />
                        ) : (
                          <span className="decision-muted">未分析</span>
                        )}
                      </td>
                      <td className="decision-muted">{formatAnalyzedAt(analyzed?.analyzed_at, { stale: analyzed?.stale })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
